import { useEffect, useState } from "react";
import { Mail, MessageSquare, Send } from "lucide-react";
import { Quote } from "@/lib/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface Props { open: boolean; onClose: () => void; quote: Quote; mode: "send" | "resend"; onConfirm: (channels: string[]) => Promise<void> }

export function SendDialog({ open, onClose, quote, mode, onConfirm }: Props) {
  const opts = (quote as any).delivery_options || { email: {}, sms: {} };
  const [email, setEmail] = useState(false);
  const [sms, setSms] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setEmail(!!opts.email?.to); setSms(!!opts.sms?.to); } }, [open]);

  const channels = [email && "email", sms && "sms"].filter(Boolean) as string[];
  const go = async () => { setBusy(true); try { await onConfirm(channels); onClose(); } finally { setBusy(false); } };

  const Row = ({ id, icon: Icon, label, to, configured, checked, set, missing }: any) => (
    <label className={`flex items-start gap-3 rounded-lg border p-3 ${to ? "cursor-pointer border-gray-200 hover:border-blue-400" : "border-dashed border-gray-200 opacity-70"}`} data-testid={`channel-${id}`}>
      <input type="checkbox" data-testid={`channel-${id}-checkbox`} className="mt-1 accent-blue-700" disabled={!to} checked={checked && !!to} onChange={(e) => set(e.target.checked)} />
      <Icon size={16} className="mt-0.5 text-blue-700" />
      <span className="min-w-0 flex-1 text-sm">
        <span className="font-medium text-gray-900">{label}</span>
        <span className="block truncate text-xs text-gray-500">{to || missing}</span>
        {to && !configured && <span className="block text-[11px] text-amber-700">Not configured yet — will be skipped with a notice</span>}
      </span>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="send-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "send" ? "Send proposal to homeowner" : "Resend proposal link"}</DialogTitle>
          <DialogDescription>{mode === "send" ? "This locks v" + quote.current_version + ", creates the share link and delivers it via the channels you pick. The link is also copied to your clipboard." : "Deliver the existing share link again."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Row id="email" icon={Mail} label="Email" to={opts.email?.to} configured={opts.email?.configured} checked={email} set={setEmail} missing="No email on the homeowner record" />
          <Row id="sms" icon={MessageSquare} label="Text message (SMS)" to={opts.sms?.to} configured={opts.sms?.configured} checked={sms} set={setSms} missing="No valid mobile number on the homeowner record" />
        </div>
        {channels.length === 0 && <p className="text-xs text-gray-500" data-testid="link-only-note">{mode === "send" ? "No channel selected — the link will just be copied so you can share it manually." : "Select at least one channel."}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="send-dialog-cancel-btn">Cancel</Button>
          <Button data-testid="send-dialog-confirm-btn" disabled={busy || (mode === "resend" && channels.length === 0)} onClick={go} className="bg-blue-700 hover:bg-blue-800"><Send size={14} className="mr-1.5" />{busy ? "Sending…" : mode === "send" ? (channels.length ? `Send via ${channels.join(" + ")}` : "Send (link only)") : `Resend via ${channels.join(" + ")}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
